import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import EventsService from "../services/EventsService";
import { format } from "date-fns";

const actionsColorMap = [
  "#1E8E3A", "#0A84FF", "#C62828", "#B8960A",
  "#8E44AD", "#E67E22", "#00BCD4", "#E91E63", "#3F51B5", "#7CB342",
];

function HomePage() {
  const token = localStorage.getItem("token");
  let username = "";

  const [events, setEvents] = useState([]);
  const [activeEvents, setActiveEvents] = useState([]);
  const [persistentActions, setPersistentActions] = useState([]);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionCounts, setActionCounts] = useState({});
  const [pulsing, setPulsing] = useState(null);
  const navigate = useNavigate();

  // Persistent action management
  const [showPersistentModal, setShowPersistentModal] = useState(false);
  const [newPersistentName, setNewPersistentName] = useState("");
  const [newPersistentColor, setNewPersistentColor] = useState("#0A84FF");

  if (token) {
    try {
      const decoded = jwtDecode(token);
      username = decoded.username;
    } catch (error) {
      console.error("Invalid token:", error.message);
    }
  }

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [allEvts, persistent, todayDigest] = await Promise.allSettled([
          EventsService.listActiveEvents().catch(() => EventsService.getAllEvents()),
          EventsService.listPersistentActions().catch(() => []),
          EventsService.getDailyDigest(format(new Date(), "yyyy-MM-dd")).catch(() => null),
        ]);

        const evts = allEvts.status === "fulfilled" ? allEvts.value : [];
        if (Array.isArray(evts)) {
          const active = evts.filter ? evts.filter((e) => e.started && !e.finished) : evts;
          setEvents(evts);
          setActiveEvents(active);
        } else if (evts && typeof evts === "object") {
          setActiveEvents(Array.isArray(evts) ? evts : []);
          setEvents(Array.isArray(evts) ? evts : []);
        }

        setPersistentActions(persistent.status === "fulfilled" ? (persistent.value || []) : []);
        setDigest(todayDigest.status === "fulfilled" ? todayDigest.value : null);
      } catch (err) {
        console.error(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const handlePersistentLog = async (paId) => {
    try {
      setPulsing(paId);
      await EventsService.logPersistentAction(paId);
      setActionCounts((prev) => ({ ...prev, [paId]: (prev[paId] || 0) + 1 }));
      setTimeout(() => setPulsing(null), 500);
    } catch (err) {
      console.error(err.message);
      setPulsing(null);
    }
  };

  const handleAddPersistent = async () => {
    if (!newPersistentName.trim()) return;
    try {
      await EventsService.createPersistentAction({
        name: newPersistentName.trim(),
        color: newPersistentColor,
      });
      setNewPersistentName("");
      const data = await EventsService.listPersistentActions();
      setPersistentActions(data || []);
    } catch (err) {
      console.error(err.message);
    }
  };

  const handleDeletePersistent = async (id) => {
    try {
      await EventsService.deletePersistentAction(id);
      setPersistentActions((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error(err.message);
    }
  };

  const handleActionClick = async (event, actionTypeId) => {
    try {
      await EventsService.addTimepoint(event.id, actionTypeId, Math.floor(Date.now() / 1000));
    } catch (err) {
      console.error(err.message);
    }
  };

  const totalEvents = events.length;
  const activeCount = activeEvents.length;

  return (
    <>
      <div className="page-header">
        <nav className="navbar navbar-expand-lg d-flex justify-content-between">
          <div className="header-title flex-fill">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              id="sidebar-toggle"
              onClick={(e) => {
                e.preventDefault();
                window.__toggleSidebar && window.__toggleSidebar(e);
              }}
            >
              <i className="fas fa-bars"></i>
            </a>
            <h5>Dashboard</h5>
          </div>
        </nav>
      </div>
      <div className="main-wrapper">
        <div style={{ marginBottom: 32 }}>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 6 }}>
            Device
          </p>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: '#fff', margin: 0 }}>
            {username || "Unknown"}
          </h2>
        </div>

        {/* Persistent Actions */}
        {persistentActions.length > 0 && (
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h6 className="event-section-title">Quick Actions</h6>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowPersistentModal(!showPersistentModal)}
              >
                <i className="fas fa-cog" style={{ marginRight: 4 }}></i>
                Manage
              </button>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {persistentActions.map((pa, i) => (
                <button
                  key={pa.id}
                  className={`persistent-action-btn ${pulsing === pa.id ? "event-action-btn-pulse" : ""}`}
                  style={{ "--action-color": pa.color || actionsColorMap[i % actionsColorMap.length] }}
                  onClick={() => handlePersistentLog(pa.id)}
                >
                  <span className="persistent-action-count">
                    {pa.todayCount || actionCounts[pa.id] || 0}
                  </span>
                  <span className="persistent-action-name">{pa.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Persistent Actions Modal */}
        {showPersistentModal && (
          <div className="glass-card" style={{ padding: 20, marginBottom: 24 }}>
            <h6 className="event-section-title" style={{ marginBottom: 12 }}>
              Manage Quick Actions
            </h6>
            {persistentActions.map((pa, i) => (
              <div key={pa.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: pa.color || actionsColorMap[i % actionsColorMap.length],
                    flexShrink: 0,
                  }}
                />
                <span style={{ flex: 1, color: "#fff", fontSize: 14 }}>{pa.name}</span>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleDeletePersistent(pa.id)}
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <input
                type="text"
                className="form-control"
                placeholder="New action name..."
                value={newPersistentName}
                onChange={(e) => setNewPersistentName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddPersistent()}
                style={{ flex: 1 }}
              />
              <input
                type="color"
                value={newPersistentColor}
                onChange={(e) => setNewPersistentColor(e.target.value)}
                style={{ width: 40, height: 38, padding: 2, border: "none", background: "transparent", cursor: "pointer" }}
              />
              <button className="btn btn-sm btn-info" onClick={handleAddPersistent}>
                Add
              </button>
            </div>
          </div>
        )}

        {/* Stats Row */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 32 }}>
          <div className="glass-card" style={{ flex: '1 1 140px', padding: 16 }}>
            <p className="glass-card-label">Total Activities</p>
            <p className="glass-card-value">{loading ? '...' : totalEvents}</p>
          </div>
          <div className="glass-card" style={{ flex: '1 1 140px', padding: 16 }}>
            <p className="glass-card-label">Active Activities</p>
            <p className="glass-card-value" style={{ color: '#30D158' }}>
              {loading ? '...' : activeCount}
            </p>
          </div>
          {digest && (
            <>
              <div className="glass-card" style={{ flex: '1 1 140px', padding: 16 }}>
                <p className="glass-card-label">Actions Today</p>
                <p className="glass-card-value" style={{ color: '#0A84FF' }}>
                  {digest.totalActions ?? "—"}
                </p>
              </div>
              <div className="glass-card" style={{ flex: '1 1 140px', padding: 16 }}>
                <p className="glass-card-label">Hours Today</p>
                <p className="glass-card-value">
                  {digest.totalHours != null ? `${digest.totalHours.toFixed(1)}` : "—"}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Active Events */}
        {activeEvents.length > 0 && (
          <div style={{ marginBottom: 28 }}>
            <h6 className="event-section-title">Active Activities</h6>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {activeEvents.map((event) => (
                <div
                  key={event.id}
                  className="glass-card active-event-card"
                  style={{ flex: '1 1 280px', maxWidth: 420 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#30D158', display: 'inline-block' }} />
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Live</span>
                    <span
                      style={{ marginLeft: "auto", fontSize: 12, color: "rgba(255,255,255,0.4)", cursor: "pointer" }}
                      onClick={() => navigate(`/event/${event.id}`)}
                    >
                      Open <i className="fas fa-arrow-right" style={{ fontSize: 10 }}></i>
                    </span>
                  </div>
                  <p style={{ fontSize: 18, fontWeight: 700, color: '#fff', margin: "0 0 12px" }}>
                    {event.name}
                  </p>
                  {event.actions && event.actions.length > 0 && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {event.actions.map((action, ai) => (
                        <button
                          key={action.id || ai}
                          className="home-action-btn"
                          style={{ "--action-color": actionsColorMap[ai % actionsColorMap.length] }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleActionClick(event, action.typeId);
                          }}
                        >
                          {action.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </>
  );
}

export default HomePage;
